import React, { useCallback, useEffect, useRef, useState } from "react";
import { Progress, UploadFile } from "antd";
import { CheckIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import styled, { css, keyframes } from "styled-components";
import { useApiUrl, useGo, useTranslate } from "@refinedev/core";
import { FileDocIcon } from "./FileDocIcon";
import { useDocumentMimeTypes } from "hooks/useDocumentMimeTypes";
import { useUploadFiles } from "#src/hooks";
import { useProjectCreation } from "pages/media/projects/hooks/useProjectCreation";
import { useProjectWizardStore } from "pages/media/projects/useProjectWizardStore";
import { AnalyticsEvents } from "types/analytics";
import { useWorkspace } from "hooks/useWorkspace/";
import { radius } from "@libertify/ui";

type DropPhase = "idle" | "hover" | "uploading" | "complete";

export interface DropZoneProps {
  onUploadStart?: () => void;
}

export const DropZone: React.FC<DropZoneProps> = ({ onUploadStart }) => {
  const [phase, setPhase] = useState<DropPhase>("idle");
  const { workspaces } = useWorkspace();
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { primaryMimeTypes } = useDocumentMimeTypes();
  const { createProject, trackEvent, paywallFlow } = useProjectCreation();
  const {
    setProject,
    setUploadedFileNames,
    setUploadedFiles,
    setWizardStartTime,
    setHasNoProjects,
    hasNoProjects,
  } = useProjectWizardStore();
  const apiUrl = useApiUrl();
  const go = useGo();
  const t = useTranslate();
  const trackingInitializedRef = useRef(false);

  // Track page init for first-time users (no projects yet)
  useEffect(() => {
    if (!trackingInitializedRef.current && hasNoProjects) {
      trackingInitializedRef.current = true;
      setWizardStartTime(Date.now());
      trackEvent(AnalyticsEvents.ProjectCreateInit, {
        page: "project_creation_wizard",
        paywall_flow: paywallFlow,
      });
    }
  }, [trackEvent, hasNoProjects, setWizardStartTime]);

  const [stagedAssets, setStagedAssets] = useState<{ uid: string }[]>([]);

  const { getS3LinksToUploadFiles, uploadFileToS3, setFileList } =
    useUploadFiles({
      assetRequestProperties: {
        // TODO: This needs to be addressed since it is hardcoded everywhere
        document_type: "Unknown",
        aggregate_documents: true,
      },
      stagedAssets,
    });

  const toUploadFile = useCallback((file: File, index: number): UploadFile => {
    return {
      uid: `dropzone-${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      type: file.type,
      originFileObj: file as unknown as UploadFile["originFileObj"],
    };
  }, []);

  const runUpload = useCallback(
    async (files: File[]) => {
      setPhase("uploading");
      onUploadStart?.();

      // Returning users: skip project creation, navigate to select-type
      if (workspaces.length > 1 || !hasNoProjects) {
        const uploadFiles = files.map((f, i) => toUploadFile(f, i));
        setUploadedFileNames(files.map((f) => f.name));
        setUploadedFiles(uploadFiles);
        setProgress(100);
        setTimeout(() => {
          setPhase("complete");
          setTimeout(() => {
            go({ to: `/media/projects/select-type` });
          }, 2500);
        }, 300);
        return;
      }

      setProgress(10);

      try {
        const project = await createProject(
          files.map((f) => f.name),
          "new_video",
          [],
          "auto",
          true
        );
        setProject(project);
        setProgress(30);
        setHasNoProjects(false);

        const uploadFiles = files.map((f, i) => toUploadFile(f, i));
        setStagedAssets(uploadFiles.map((f) => ({ uid: f.uid })));
        setFileList(uploadFiles);

        const s3Links = await getS3LinksToUploadFiles({
          fileListToUpload: uploadFiles,
          customUrl: (fileName: string) =>
            `${apiUrl}/media/projects/${project.id}/documents/${fileName}`,
        });

        await Promise.all(uploadFileToS3(uploadFiles, s3Links));

        setProgress(100);
        setTimeout(() => {
          setPhase("complete");
          setTimeout(() => {
            go({ to: `/media/projects/${project.id}/slides-streaming` });
          }, 2500);
        }, 300);
      } catch (error) {
        console.error("Error creating project:", error);
        setPhase("idle");
        setProgress(0);
      }
    },
    [
      onUploadStart,
      hasNoProjects,
      setUploadedFileNames,
      setUploadedFiles,
      setProgress,
      createProject,
      setProject,
      toUploadFile,
      setFileList,
      getS3LinksToUploadFiles,
      uploadFileToS3,
      apiUrl,
      go,
      setHasNoProjects,
      workspaces.length,
    ]
  );

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (phase === "idle") setPhase("hover");
    },
    [phase]
  );

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (phase !== "hover") return;
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      setPhase("idle");
    },
    [phase]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (phase !== "idle" && phase !== "hover") return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;
      setPhase("hover");
      setTimeout(() => void runUpload(files), 500);
    },
    [phase, runUpload]
  );

  const onFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length === 0) return;
      setPhase("hover");
      setTimeout(() => void runUpload(files), 500);
      e.target.value = "";
    },
    [runUpload]
  );

  const onClick = useCallback(() => {
    if (phase !== "idle") return;
    fileInputRef.current?.click();
  }, [phase]);

  return (
    <Root>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={primaryMimeTypes}
        onChange={onFileSelected}
        style={{ display: "none" }}
      />
      <OuterContainer $active={phase === "hover"}>
        <Wrapper
          $phase={phase}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={onClick}
        >
          {/* ── IDLE / HOVER ── */}
          {(phase === "idle" || phase === "hover") && (
            <ContentFade>
              <IconsContainer>
                <div className="file file-pdf">
                  <FileDocIcon type="pdf" />
                </div>
                <div className="file file-ppt">
                  <FileDocIcon type="ppt" />
                </div>
                <div className="file file-doc">
                  <FileDocIcon type="doc" />
                </div>
              </IconsContainer>
              <DownloadSimpleIcon
                size={20}
                color={phase === "hover" ? "#14B8A6" : "#9CA3AF"}
                style={{ marginBottom: 10 }}
              />
              <DropLabel $hover={phase === "hover"}>
                {t(
                  phase === "idle"
                    ? "components.dropZone.dropDocument"
                    : "components.dropZone.dropHere"
                )}{" "}
                <span className="browse">
                  {t("components.dropZone.browse")}
                </span>
              </DropLabel>
            </ContentFade>
          )}

          {/* ── UPLOADING ── */}
          {phase === "uploading" && (
            <ContentFade style={{ width: "80%" }}>
              <Progress
                percent={Math.round(progress)}
                strokeColor="#14B8A6"
                trailColor="#CCFBF1"
                strokeWidth={8}
                showInfo={false}
              />
              <span
                style={{
                  fontSize: 14,
                  color: "#6B7280",
                  textAlign: "center",
                  marginTop: 10,
                }}
              >
                {t("components.dropZone.uploading")}
              </span>
            </ContentFade>
          )}

          {/* ── COMPLETE ── */}
          {phase === "complete" && (
            <ContentFade style={{ alignItems: "center" }}>
              <CheckRing>
                <CheckIcon size={24} color="#14B8A6" weight="bold" />
              </CheckRing>
              <span style={{ fontSize: 14, color: "#6B7280", marginTop: 10 }}>
                {t("components.dropZone.complete")}
              </span>
              <span style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>
                {t("components.dropZone.autoStart")}
              </span>
            </ContentFade>
          )}
        </Wrapper>
      </OuterContainer>
    </Root>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const checkIn = keyframes`
  from { transform: scale(0.8); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const OuterContainer = styled.div<{ $active: boolean }>`
  display: inline-flex;
  border-radius: ${radius.box}px;
  background: #fff;
  padding: 0;
  transition:
    padding 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.35s ease;

  ${(p) =>
    p.$active &&
    css`
      padding: 10px;
      box-shadow: 0 4px 60px -25px #3bd4ca;

      .file-pdf {
        transform: rotate(-10deg) translateX(-7px) translateY(-7px);
      }

      .file-ppt {
        transform: translateX(-50%) translateY(-10px);
      }

      .file-doc {
        transform: rotate(10deg) translateX(7px) translateY(-7px);
      }
    `}
`;

const Wrapper = styled.div<{ $phase: DropPhase }>`
  position: relative;
  width: 462px;
  max-width: calc(100vw - 32px);
  border-radius: ${radius.box}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition:
    height 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
    border-color 0.35s ease,
    background 0.35s ease,
    transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);

  ${(p) =>
    p.$phase === "idle" &&
    css`
      height: 210px;
      border: 1.5px dashed #d1d5db;
      background: #ffffff;
      cursor: pointer;

      &:hover {
        border-color: #5eead4;
        background: rgba(204, 251, 241, 0.25);
      }
    `}

  ${(p) =>
    p.$phase === "hover" &&
    css`
      height: 210px;
      border: 1.5px dashed #5eead4;
      background: #ccfbf1;
      transform: scale(1.015);
      cursor: default;
    `}

  ${(p) =>
    p.$phase === "uploading" &&
    css`
      height: 120px;
      border: 1.5px dashed #5eead4;
      background: #f0fdfb;
      cursor: default;
    `}

  ${(p) =>
    p.$phase === "complete" &&
    css`
      height: 150px;
      border: 1.5px dashed #5eead4;
      background: #f0fdfb;
      cursor: default;
    `}
`;

const IconsContainer = styled.div`
  position: relative;
  width: 140px;
  height: 74px;
  margin-bottom: 16px;

  .file {
    position: absolute;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .file-pdf {
    left: 0;
    bottom: 0;
    transform: rotate(-10deg);
  }

  .file-ppt {
    left: 50%;
    bottom: 8px;
    z-index: 2;
    transform: translateX(-50%);
  }

  .file-doc {
    right: 0;
    bottom: 0;
    transform: rotate(10deg);
  }
`;

const DropLabel = styled.span<{ $hover: boolean }>`
  font-size: 13px;
  color: ${(p) => (p.$hover ? "#14B8A6" : "#6B7280")};
  font-weight: ${(p) => (p.$hover ? 500 : 400)};

  .browse {
    color: #14b8a6;
    font-weight: ${(p) => (p.$hover ? 700 : 500)};
    cursor: pointer;
  }
`;

const ContentFade = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.28s ease both;
`;

const CheckRing = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${radius.circle};
  border: 2.5px solid #14b8a6;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${checkIn} 0.4s ease both;
`;
